import React, { Fragment } from 'react';
import moment from 'moment';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import { Grid, TextField, InputAdornment, MenuItem } from '@material-ui/core';
import { fieldDateFormat, backendDateFormat } from '../../../constants';
import { regexConstants } from '../../../constants/regEx';
import { checkValuationAdminFn } from '../../../utils';
import { outlinedLabel } from '../../Elements/components/OutlinedFieldLabel';
import { mapFieldGuidance } from '../mockEngineValuation';

const EngineValuationFields = ({
  mode,
  error,
  valuationCrud,
  onFieldChange,
  updateErrorField,
  engineTypes,
  qecOptions = [],
  shopVisitOptions = [],
  conditionOptions = [],
  fieldGuidance,
  conditionNote = '',
  inflationRateMin,
  inflationRateMax,
}) => {
  const disabled = mode === 'view';
  const showInternal = checkValuationAdminFn(); // security group 46 = valuation team
  const showProjection = parseInt(valuationCrud.utility_type) === 2 || valuationCrud.is_future_blr;
  const projectionMoment = valuationCrud.projection_to
    ? (moment(valuationCrud.projection_to, backendDateFormat, true).isValid()
      ? moment(valuationCrud.projection_to, backendDateFormat, true)
      : moment(valuationCrud.projection_to))
    : null;
  const projectionNotFuture = !!(
    projectionMoment
    && projectionMoment.isValid()
    && projectionMoment.clone().startOf('day').diff(moment().startOf('day'), 'days') <= 0
  );
  // Prefer future-date message whenever a non-future value is present (edit/create),
  // so a stale "required" error cannot mask the correct validation text.
  const projectionHelper = projectionNotFuture
    ? 'Projection date must be a future date'
    : (error.projection_to || '');
  const tips = fieldGuidance || mapFieldGuidance({});
  const inflationTipBase = tips.escalation_percentage || tips.inflation_rate || '';
  const hasInflationRange = inflationRateMin != null && inflationRateMax != null;
  const inflationRangeText = hasInflationRange
    ? `Allowed range: ${Number(inflationRateMin).toFixed(1)}%–${Number(inflationRateMax).toFixed(1)}%.`
    : '';
  const inflationTip = [inflationTipBase, inflationRangeText].filter(Boolean).join(' ');
  const inflationPlaceholder = hasInflationRange
    ? `Enter value between ${Number(inflationRateMin).toFixed(1)}%–${Number(inflationRateMax).toFixed(1)}%`
    : 'Enter value between 0.0%–5.0%';
  const hasEngineType = !!(valuationCrud.engine_type && valuationCrud.engine_type.id);
  const qecEmptyHint = hasEngineType
    ? 'No QEC options for this engine type'
    : 'Select Engine Type to load QEC options';
  const shopVisitEmptyHint = hasEngineType
    ? 'No shop visit options for this engine type'
    : 'Select Engine Type to load shop visit options';

  return (
    <Fragment>
      <Grid item md={4} xs={12}>
        <TextField
          id="serial_number"
          label={outlinedLabel('Serial Number', { tip: tips.serial_number })}
          fullWidth
          margin="normal"
          disabled={disabled}
          placeholder="Enter Serial Number"
          InputLabelProps={{ shrink: true }}
          value={valuationCrud.serial_number || ''}
          onChange={(e) => {
            onFieldChange(e, 'serial_number', e.target.value);
            updateErrorField('serial_number', '');
          }}
          variant="outlined"
          autoComplete="off"
        />
      </Grid>
      <Grid item md={4} xs={12}>
        <Autocomplete
          disabled={disabled}
          options={engineTypes}
          getOptionLabel={option => option.name || ''}
          id="engine_type"
          value={valuationCrud.engine_type || null}
          onChange={(e, value) => { onFieldChange(e, 'engine_type', value); updateErrorField('engine_type', ''); }}
          renderInput={params => (
            <TextField
              {...params}
              label={outlinedLabel('Engine Type', { required: true, tip: tips.engine_type })}
              error={!!error.engine_type}
              helperText={error.engine_type || ''}
              onFocus={() => updateErrorField('engine_type', '')}
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              placeholder="Select Engine Type"
            />
          )}
        />
      </Grid>

      <Grid item md={4} xs={12}>
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <KeyboardDatePicker
            disabled={disabled}
            margin="normal"
            id="date_of_manufacture"
            label={outlinedLabel('Date of Manufacture', { tip: tips.date_of_manufacture })}
            placeholder="DD-MM-YYYY"
            format={fieldDateFormat}
            fullWidth
            InputLabelProps={{ shrink: true }}
            minDate={moment().subtract(40, 'years')}
            maxDate={moment().add(2, 'years').endOf('year')}
            onChange={(data, value) => onFieldChange(value, 'date_of_manufacture', data ? moment(data).format(backendDateFormat) : data)}
            value={valuationCrud.date_of_manufacture}
            error={!!error.date_of_manufacture}
            helperText={error.date_of_manufacture || ''}
            inputVariant="outlined"
            autoComplete="off"
          />
        </MuiPickersUtilsProvider>
      </Grid>

      <Grid item md={4} xs={12}>
        <Autocomplete
          disabled={disabled || !qecOptions.length}
          options={qecOptions}
          getOptionLabel={option => option.label || option.name || ''}
          getOptionSelected={(option, value) => (
            (option.id != null && value && option.id === value.id)
            || (option.value != null && value && option.value === value.value)
          )}
          id="qec_type"
          value={valuationCrud.qec_type || null}
          onChange={(e, value) => { onFieldChange(e, 'qec_type', value); updateErrorField('qec_type', ''); }}
          renderInput={params => (
            <TextField
              {...params}
              label={outlinedLabel('QEC Type', { required: !!qecOptions.length, tip: tips.qec_type })}
              error={!!error.qec_type}
              helperText={error.qec_type || (!qecOptions.length ? qecEmptyHint : '')}
              onFocus={() => updateErrorField('qec_type', '')}
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              placeholder="Select QEC Type"
            />
          )}
        />
      </Grid>

      <Grid item md={4} xs={12}>
        <Autocomplete
          disabled={disabled || !shopVisitOptions.length}
          options={shopVisitOptions}
          getOptionLabel={option => option.label || option.name || ''}
          getOptionSelected={(option, value) => {
            const optVal = option.code != null ? option.code : option.value;
            const curVal = value && (value.code != null ? value.code : value.value);
            return optVal === curVal;
          }}
          id="shop_visit_status"
          value={
            shopVisitOptions.find((opt) => {
              const optVal = opt.code != null ? opt.code : opt.value;
              return String(optVal) === String(valuationCrud.shop_visit_status);
            }) || null
          }
          onChange={(e, value) => {
            const next = value
              ? (value.code != null ? value.code : value.value)
              : null;
            onFieldChange(e, 'shop_visit_status', next);
            updateErrorField('shop_visit_status', '');
          }}
          renderInput={params => (
            <TextField
              {...params}
              label={outlinedLabel('Shop Visit Status', { required: true, tip: tips.shop_visit_status })}
              error={!!error.shop_visit_status}
              helperText={error.shop_visit_status || (!shopVisitOptions.length ? shopVisitEmptyHint : '')}
              onFocus={() => updateErrorField('shop_visit_status', '')}
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              placeholder="Select Shop Visit Status"
            />
          )}
        />
      </Grid>

      <Grid item md={4} xs={12}>
        <Autocomplete
          disabled={disabled || !conditionOptions.length}
          options={conditionOptions}
          getOptionLabel={option => option.label || ''}
          getOptionSelected={(option, value) => option.value === value.value}
          id="condition"
          value={
            conditionOptions.find(opt => opt.value === valuationCrud.condition) || null
          }
          onChange={(e, value) => {
            onFieldChange(e, 'condition', value ? value.value : null);
            updateErrorField('condition', '');
          }}
          renderInput={params => (
            <TextField
              {...params}
              label={outlinedLabel('Condition', { required: true, tip: tips.condition })}
              error={!!error.condition}
              helperText={error.condition || ''}
              onFocus={() => updateErrorField('condition', '')}
              margin="normal"
              fullWidth
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              placeholder="Select Condition"
            />
          )}
        />
      </Grid>

      <Grid item md={4} xs={12}>
        <TextField
          id="escalation_percentage"
          label={outlinedLabel('Inflation Rate', { required: true, tip: inflationTip })}
          placeholder={inflationPlaceholder}
          fullWidth
          margin="normal"
          disabled={disabled}
          InputLabelProps={{ shrink: true }}
          value={valuationCrud.escalation_percentage != null ? valuationCrud.escalation_percentage : ''}
          onChange={(e) => {
            if (regexConstants.numberWithDot.test(e.target.value) || e.target.value === '') {
              onFieldChange(e, 'escalation_percentage', e.target.value);
              updateErrorField('escalation_percentage', '');
            }
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
          variant="outlined"
          error={!!error.escalation_percentage}
          helperText={error.escalation_percentage || ''}
          autoComplete="off"
        />
      </Grid>

      {conditionNote ? (
        <Grid item xs={12}>
          <p className="engine-cond-note">{conditionNote}</p>
        </Grid>
      ) : null}

      {showInternal ? (
        <Grid item xs={12}>
          <Grid container spacing={1}>
            <Grid item md={4} xs={12}>
              <TextField
                id="qec_discount"
                label={outlinedLabel('QEC Discount', { tip: tips.qec_discount, badge: 'Internal' })}
                placeholder="Enter QEC Discount"
                fullWidth
                margin="normal"
                disabled={disabled}
                InputLabelProps={{ shrink: true }}
                value={valuationCrud.qec_discount != null ? valuationCrud.qec_discount : ''}
                onChange={(e) => {
                  if (regexConstants.numberWithDot.test(e.target.value) || e.target.value === '') {
                    onFieldChange(e, 'qec_discount', e.target.value);
                  }
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                variant="outlined"
                autoComplete="off"
              />
            </Grid>
            <Grid item md={4} xs={12}>
              <TextField
                select
                id="premium_for_new"
                label={outlinedLabel('Premium For New', { tip: tips.premium_for_new, badge: 'Internal' })}
                placeholder="Select Premium For New"
                fullWidth
                margin="normal"
                disabled={disabled}
                InputLabelProps={{ shrink: true }}
                value={valuationCrud.premium_for_new || 'no'}
                onChange={(e) => onFieldChange(e, 'premium_for_new', e.target.value)}
                variant="outlined"
              >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </TextField>
            </Grid>
            <Grid item md={4} xs={12}>
              <TextField
                id="premium_pct"
                label={outlinedLabel('Premium %', { tip: tips.premium_pct || tips.premium_percentage, badge: 'Internal' })}
                placeholder="Enter Premium %"
                fullWidth
                margin="normal"
                disabled={disabled || (valuationCrud.premium_for_new || 'no') !== 'yes'}
                InputLabelProps={{ shrink: true }}
                value={valuationCrud.premium_pct != null ? valuationCrud.premium_pct : ''}
                onChange={(e) => {
                  if (regexConstants.numberWithDot.test(e.target.value) || e.target.value === '') {
                    onFieldChange(e, 'premium_pct', e.target.value);
                  }
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                variant="outlined"
                autoComplete="off"
              />
            </Grid>
          </Grid>
        </Grid>
      ) : null}

      {showProjection ? (
        <Grid item md={4} xs={12}>
          <MuiPickersUtilsProvider utils={MomentUtils}>
            <KeyboardDatePicker
              margin="normal"
              id="projection_to"
              label={outlinedLabel('Projection To', { required: true, tip: tips.projection_to })}
              format={fieldDateFormat}
              disabled={disabled}
              fullWidth
              disablePast
              minDate={moment().add(1, 'days')}
              maxDate={valuationCrud.base_value_date ? moment(valuationCrud.base_value_date).add(10, 'years').endOf('year') : moment().add(10, 'years').endOf('year')}
              minDateMessage="Projection date must be a future date"
              invalidDateMessage="Please enter Valid Date"
              InputLabelProps={{ shrink: true }}
              onChange={(data, value) => {
                // Keep date in state even when picker rejects it (today/past vs minDate),
                // so UI/submit show "must be a future date" instead of false "required"
                let next = null;
                if (data && moment(data).isValid()) {
                  next = moment(data).format(backendDateFormat);
                } else if (typeof value === 'string' && value.trim()) {
                  const parsed = moment(value, fieldDateFormat, true);
                  if (parsed.isValid()) {
                    next = parsed.format(backendDateFormat);
                  } else {
                    return; // incomplete typing — don't wipe existing value
                  }
                } else if (value === '') {
                  next = null; // user cleared the input
                } else {
                  return; // picker null (e.g. minDate) — keep existing value
                }
                onFieldChange(value, 'projection_to', next);
                if (next) {
                  const proj = moment(next, backendDateFormat, true);
                  const notFuture = proj.isValid()
                    && proj.clone().startOf('day').diff(moment().startOf('day'), 'days') <= 0;
                  updateErrorField(
                    'projection_to',
                    notFuture ? 'Projection date must be a future date' : ''
                  );
                } else {
                  updateErrorField('projection_to', '');
                }
              }}
              value={valuationCrud.projection_to}
              error={!!error.projection_to || projectionNotFuture}
              helperText={projectionHelper}
              onFocus={() => {
                if (!projectionNotFuture) {
                  updateErrorField('projection_to', '');
                }
              }}
              inputVariant="outlined"
              placeholder="DD-MM-YYYY"
              autoComplete="off"
            />
          </MuiPickersUtilsProvider>
        </Grid>
      ) : null}
    </Fragment>
  );
};

export default EngineValuationFields;
